/**
 * @file EditScheduledMeetingSheet.tsx
 * @brief Edit a scheduled meeting before, during and after it (B48).
 *
 * @details
 * DES 02-11-2026 user feedback: nobody could add an agenda item or a person
 * once the meeting had started. What may change now comes from the server
 * (`editable`, `edit_phase`, BACKEND meetings/editWindow.mjs):
 *
 * - before: title, people, agenda, time;
 * - during (the recording runs): title, people, agenda; the time is read-only;
 * - after (the recording stopped): title and people; the agenda is locked (it
 *   is what the meeting was checked against) with a pointer to adding a note
 *   to the meeting instead;
 * - cancelled: the menu does not offer Edit.
 *
 * People: anyone whose access was taken back (B45) is not shown and not sent
 * back. A company member is picked from the list; someone new needs a name
 * AND an email (AddPersonForm, the people rule). People already on the
 * meeting are echoed back exactly as the server sent them so it matches them.
 * Taken off after the start = their access is taken back (Who has access can
 * add them again). No invite email once the meeting has happened (server).
 *
 * Languages are not edited here (set at setup); an older server without
 * `editable` gets today's "before" rules.
 *
 * @references
 * - docs/product/meeting-edit-after-start-b48.md
 * - scheduledSide.ts (meetingEditBody, editRosterRows)
 * - FRONTEND twin: B48-P
 *
 * @author DES
 * @date 02-11-2026
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ChevronDown, ListOrdered, LoaderCircle, Lock, NotebookPen, PenLine, UserPlus, X } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { useAssignablePeople } from "@/features/company-meetings/api";
import { NotePickerSheet, type PickedNote } from "@/features/notes/NotePickerSheet";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import { AddPersonForm } from "./AddPersonForm";
import { MAX_AGENDA_NOTES } from "./capture/boardroomDraftStore";
import {
  meetingEditErrorMessage,
  updateScheduledMeeting,
  type MeetingEditable,
  type ScheduledMeeting,
} from "./scheduledMeetingsApi";
import { editRosterRows, meetingEditBody, type EditRosterRow } from "./scheduledSide";
import { useBoardroomBasePath } from "./useBoardroomBasePath";

/** Today's rules for a server that sends no `editable` (all open). */
const BEFORE: MeetingEditable = { title: true, participants: true, agenda: true, time: true, languages: true };

const PHASE_LINE: Record<string, string> = {
  before: "Change the title, people, agenda or time.",
  during: "The meeting is being recorded: the title, people and agenda can still change. The time can't.",
  after: "The meeting has happened: the title and people can still change.",
};

export function EditScheduledMeetingSheet({
  open,
  onOpenChange,
  meeting,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: ScheduledMeeting | null;
  onSaved: (meeting: ScheduledMeeting) => void;
}) {
  const navigate = useNavigate();
  const base = useBoardroomBasePath();
  const editable = meeting?.editable ?? BEFORE;
  const phase = meeting?.edit_phase ?? "before";
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<EditRosterRow[]>([]);
  const [agenda, setAgenda] = useState<PickedNote[]>([]);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !meeting) return;
    setTitle(meeting.title ?? "");
    setWhen(meeting.scheduled_at);
    setRows(editRosterRows(meeting.participants ?? [], meeting.organizer_id));
    const ids = meeting.prep_note_ids?.length
      ? meeting.prep_note_ids
      : meeting.prep_note_id
        ? [meeting.prep_note_id]
        : [];
    const titles = new Map((meeting.prep_notes ?? []).map((n) => [n.id, n.title ?? ""]));
    setAgenda(ids.map((id) => ({ id, title: titles.get(id) || "Agenda" })));
  }, [open, meeting]);

  const onSave = async () => {
    if (!meeting || saving) return;
    if (editable.title && !title.trim()) {
      toast({ variant: "destructive", title: "Title required", description: "Give the meeting a title." });
      return;
    }
    if (editable.time && phase === "before" && when && new Date(when).getTime() < Date.now()) {
      toast({ variant: "destructive", title: "Invalid time", description: "Please pick a time in the future." });
      return;
    }
    const body = meetingEditBody(meeting, editable, {
      title,
      scheduledAt: when,
      rows,
      prepNoteIds: agenda.map((n) => n.id),
    });
    setSaving(true);
    try {
      const saved = await updateScheduledMeeting(meeting.id, body);
      onSaved(saved);
      onOpenChange(false);
      toast({
        variant: "success",
        title: "Meeting updated",
        description:
          phase === "before" ? "Invitees will get an updated calendar invite." : undefined,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Could not save",
        description:
          meetingEditErrorMessage(err) ??
          (err instanceof ApiClientError || err instanceof Error ? err.message : "Please try again."),
      });
    } finally {
      setSaving(false);
    }
  };

  const addNote = () => {
    if (!meeting?.boardroom_meeting_id) return;
    onOpenChange(false);
    navigate(`${base}/meetings/${meeting.boardroom_meeting_id}`, { state: { scrollTo: "notes" } });
  };

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Edit meeting</FormSheetTitle>
            <FormSheetDescription>{PHASE_LINE[phase] ?? PHASE_LINE.before}</FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody>
            <FormSheetSection title="Meeting">
              <FormSheetFields>
                <FormSheetField>
                  <FormFieldGroup label="Title" htmlFor="edit-meeting-title" required={editable.title}>
                    <Input
                      capitalizeFirst
                      id="edit-meeting-title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      readOnly={!editable.title}
                      maxLength={200}
                      className="font-semibold"
                    />
                  </FormFieldGroup>
                </FormSheetField>
                <FormSheetField>
                  <FormFieldGroup
                    label="Starts at"
                    hint={editable.time ? undefined : "Can't change once the meeting has started."}
                  >
                    {editable.time ? (
                      <DateTimePicker
                        value={when}
                        onChange={setWhen}
                        title="Boardroom start"
                        disablePast
                        description="Pick a future date and time."
                      />
                    ) : (
                      <p className="flex h-11 items-center gap-2 px-1 text-sm text-muted-foreground">
                        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {meeting ? format(new Date(meeting.scheduled_at), "EEEE, d MMMM · h:mm a") : ""}
                      </p>
                    )}
                  </FormFieldGroup>
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>

            <FormSheetSection
              title="People"
              subtitle={
                phase === "before"
                  ? "Invitees get the calendar invite."
                  : "Added now: they can open the meeting and are offered when naming speakers. No invite email is sent."
              }
            >
              <FormSheetFields>
                <FormSheetField span={2}>
                  <PeopleEditor
                    rows={rows}
                    setRows={setRows}
                    disabled={!editable.participants || saving}
                    organizerName={meeting?.organizer_name ?? null}
                    organizerId={meeting?.organizer_id ?? null}
                    phase={phase}
                  />
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>

            <FormSheetSection title="Agenda">
              <FormSheetFields>
                <FormSheetField span={2}>
                  {editable.agenda ? (
                    <NotePickerSheet
                      anchor={
                        <button
                          type="button"
                          aria-label="Choose agendas"
                          className="flex h-11 w-full items-center gap-2 rounded-full border border-input bg-field px-4 text-left text-sm hover:bg-muted/50"
                        >
                          {agenda.length > 0 ? (
                            <NotebookPen className="h-4 w-4 shrink-0 text-brand" aria-hidden />
                          ) : (
                            <ListOrdered className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                          )}
                          <span className={cn("min-w-0 flex-1 truncate", agenda.length === 0 && "text-muted-foreground")}>
                            {agenda.length === 0
                              ? "Pick agendas"
                              : agenda.map((n) => n.title || "Untitled agenda").join(", ")}
                          </span>
                          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                        </button>
                      }
                      open={agendaOpen}
                      onOpenChange={setAgendaOpen}
                      selectedId={null}
                      onPick={() => {}}
                      multiple
                      max={MAX_AGENDA_NOTES}
                      selectedIds={agenda.map((n) => n.id)}
                      onToggle={(picked) =>
                        setAgenda((cur) =>
                          cur.some((n) => n.id === picked.id)
                            ? cur.filter((n) => n.id !== picked.id)
                            : cur.length >= MAX_AGENDA_NOTES
                              ? cur
                              : [...cur, picked],
                        )
                      }
                    />
                  ) : (
                    // After the meeting (DES 02-11-2026): read-only, with the
                    // way to add to it now.
                    <div className="space-y-2">
                      <p className="flex items-center gap-2 text-sm text-foreground">
                        <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                        {agenda.length === 0
                          ? "No agenda"
                          : agenda.map((n) => n.title || "Untitled agenda").join(", ")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        The agenda is locked after the meeting: it is what the meeting was checked
                        against. Add a note to the meeting instead.
                      </p>
                      {meeting?.boardroom_meeting_id ? (
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addNote}>
                          <PenLine className="mr-1.5 h-3.5 w-3.5" />
                          Add a note to the meeting
                        </Button>
                      ) : null}
                    </div>
                  )}
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>
          </FormSheetBody>

          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={saving}
                className={formSheetActionClass}
              >
                Cancel
              </Button>
            </FormSheetFooterCell>
            <FormSheetFooterCell>
              <Button
                type="button"
                onClick={() => void onSave()}
                disabled={saving}
                className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
              >
                {saving ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}

/** The roster: the organizer, everyone on it (✕ to take off), then Add. */
function PeopleEditor({
  rows,
  setRows,
  disabled,
  organizerName,
  organizerId,
  phase,
}: {
  rows: EditRosterRow[];
  organizerId: string | null;
  setRows: (fn: (cur: EditRosterRow[]) => EditRosterRow[]) => void;
  disabled: boolean;
  organizerName: string | null;
  phase: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const people = useAssignablePeople(open);

  const takenEmails = rows.map((r) => r.email).filter(Boolean);
  const takenIds = new Set(
    [
      organizerId ?? "",
      ...rows.map((r) => String(r.saved?.userId ?? (r.add && "userId" in r.add ? r.add.userId : "")) || ""),
    ].filter(Boolean),
  );
  const items: SearchableMenuItem[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (people.data ?? [])
      .filter((p) => !takenIds.has(p.id) && !takenEmails.some((e) => e.toLowerCase() === String(p.email ?? "").toLowerCase()))
      .filter((p) => !q || `${p.name ?? ""} ${p.email ?? ""}`.toLowerCase().includes(q))
      .map((p) => ({
        value: p.id,
        label: p.name || p.email || "Colleague",
        description: p.name && p.email ? p.email : undefined,
        icon: <Avatar src={p.avatarUrl} initials={getInitials(p.name, null, p.email)} alt="" size="sm" />,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people.data, query, rows]);

  const close = () => {
    setOpen(false);
    setAdding(false);
    setQuery("");
  };
  const typed = query.trim();

  return (
    <div className="space-y-1">
      <ul className="divide-y divide-border/60">
        {organizerName ? (
          <li className="flex items-center gap-3 py-2">
            <Avatar initials={getInitials(organizerName)} alt="" size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{organizerName}</span>
              <span className="block text-xs text-muted-foreground">Organiser</span>
            </span>
          </li>
        ) : null}
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-3 py-2">
            <Avatar src={r.profileImageUrl ?? undefined} initials={getInitials(r.name)} alt="" size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{r.name}</span>
              {/* Never an email in a name slot; none = "Guest" (B48). */}
              <span className="block truncate text-xs text-muted-foreground">
                {r.email || "Guest"}
                {r.add ? " · new" : ""}
              </span>
            </span>
            {disabled ? null : (
              <button
                type="button"
                onClick={() => setRows((cur) => cur.filter((x) => x.key !== r.key))}
                aria-label={`Take ${r.name} off this meeting`}
                title={phase === "before" ? "Remove" : "Take off (their access is taken back)"}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {disabled ? null : (
        <DropdownMenu open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="mt-1 rounded-full">
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
              Add people
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80 p-1.5">
            <SearchableMenuList
              title="Add people"
              searchPlaceholder="Search, or type a new name"
              items={adding ? [] : items}
              query={query}
              onQueryChange={(v) => {
                setQuery(v);
                setAdding(false);
              }}
              loading={people.isLoading}
              noMatchText="No colleagues match. Add them as someone new above."
              header={
                !typed ? null : adding ? (
                  <div className="px-1 pb-2">
                    <AddPersonForm
                      initialName={typed.includes("@") ? "" : typed}
                      takenEmails={takenEmails}
                      onCancel={() => setAdding(false)}
                      onAdd={(p) => {
                        const member = (people.data ?? []).find(
                          (x) => String(x.email ?? "").toLowerCase() === p.email.toLowerCase(),
                        );
                        setRows((cur) => [
                          ...cur,
                          member
                            ? {
                                key: `add:${member.id}`,
                                name: member.name || p.name,
                                email: member.email ?? p.email,
                                isExternal: false,
                                profileImageUrl: member.avatarUrl ?? null,
                                add: { userId: member.id },
                              }
                            : {
                                key: `add:${p.email.toLowerCase()}`,
                                name: p.name,
                                email: p.email,
                                isExternal: true,
                                add: { name: p.name, email: p.email },
                              },
                        ]);
                        close();
                      }}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                      <UserPlus className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      Add someone: <span className="font-semibold">{typed}</span>
                    </span>
                  </button>
                )
              }
              onSelect={(id) => {
                const p = (people.data ?? []).find((x) => x.id === id);
                if (!p) return;
                setRows((cur) => [
                  ...cur,
                  {
                    key: `add:${p.id}`,
                    name: p.name || p.email || "Colleague",
                    email: p.email ?? "",
                    isExternal: false,
                    profileImageUrl: p.avatarUrl ?? null,
                    add: { userId: p.id },
                  },
                ]);
                close();
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
